// src/components/CreateOrder.js
import OrderOutcome from "./OrderOutcome";
import VerifyBanner from "./VerifyBanner";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "./Navigation";
import useOnlineStatus from "../pwa/useOnlineStatus";
import RecipientSelector from "./RecipientSelector";
import {
  createOrderQuote,
  createParentOrder,
  getProfile,
  getWalletBalance,
  fundParentOrder,
  fulfilParentOrder,
  getParentOrder,
} from "../services/api";

/**
 * Send Money (New) — the money-only parent-order journey.
 *
 * ADR-001: customer journeys are product-separated while fulfilment matures.
 * This flow handles REMITTANCE items only. Airtime lives in Mobile Top-Up.
 * The two are connected by a post-confirmation hand-off that creates a
 * SEPARATE quote and Parent Order (an accepted quote is never modified).
 *
 * Flow:
 *   1. Choose payment journey    SNPL (credit) OR Wallet/SNPN (balance)
 *   2. Wallet recipient count    1 / 2 / 3; skipped for SNPL
 *   3. Amount direction          send CAD OR recipient receives KES
 *   4. Recipients + amounts      with a live cap counter
 *   5. Review quote -> Confirm -> upsell hand-off
 *
 * Money rules enforced:
 *  - No mixing: an order is EITHER SNPL OR wallet-funded, never both.
 *  - SNPL does not draw on the wallet; it is a separate credit line.
 *  - The order total must fit the chosen funding source's available amount.
 *  - A recipient may not appear twice in the same order.
 */

const MAX_RECIPIENTS = 3;

const LIMITS = {
  MONEY_KES_MAX: 150000, // M-Pesa per-transaction ceiling
  MONEY_KES_MIN: 100,
  CAD_MIN: 1,
};

// fallback used only for the live estimate when entering KES; the quote is authoritative
const FALLBACK_RATE = 92.25; // KES per CAD

export default function CreateOrder() {
  const online = useOnlineStatus(); // money actions need a live connection
  const navigate = useNavigate();

  const [step, setStep] = useState(1);

  // funding context
  const [snplAvailable, setSnplAvailable] = useState(null);
  const [snplStatus, setSnplStatus] = useState(null);
  const [walletCad, setWalletCad] = useState(null);
  const [contextLoading, setContextLoading] = useState(true);
  const [verification, setVerification] = useState(null);

  // choices
  const [recipientCount, setRecipientCount] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null); // 'SNPL' | 'WALLET'
  // Set when a chosen SNPL selection is withdrawn because a second recipient
  // was added. Without this the option silently disappears and the balance
  // banner switches to wallet with no explanation.
  const [snplCleared, setSnplCleared] = useState(false);
  const [direction, setDirection] = useState("SEND_CAD"); // 'SEND_CAD' | 'RECEIVE_KES'

  // recipients + amounts
  const [slots, setSlots] = useState([]);
  const [amounts, setAmounts] = useState({}); // { [slotIndex]: "20000" }

  // results
  const [quote, setQuote] = useState(null);
  const [order, setOrder] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    loadContext();
  }, []);

  async function loadContext() {
    setContextLoading(true);
    try {
      const profile = await getProfile();
      const u = (profile.data && profile.data.user) || profile.user || profile || {};
      setVerification({ idVerified: Boolean(u.idVerified), phoneVerified: Boolean(u.phoneVerified) });
      const limit = Number(u.snplLimit || 0);
      const available = u.snplAvailable != null ? Number(u.snplAvailable) : limit;
      setSnplAvailable(available);
      setSnplStatus((u.snplStatus || (limit > 0 ? "APPROVED" : "UNASSESSED")).toUpperCase());
    } catch (e) {
      setSnplAvailable(0);
    }
    try {
      const wallet = await getWalletBalance();
      // TD-15: spendable = total minus money reserved for orders in progress.
      const spendable = wallet.available && wallet.available.CAD != null ? wallet.available.CAD
        : (wallet.balances && wallet.balances.CAD);
      setWalletCad(Number(spendable || 0));
    } catch (e) {
      setWalletCad(0);
    }
    setContextLoading(false);
  }

  // ---------- funding ----------

  const availableCap =
    paymentMethod === "SNPL"
      ? Number(snplAvailable || 0)
      : paymentMethod === "WALLET"
      ? Number(walletCad || 0)
      : 0;

  const MIN_SNPL_CAD = 5;
  const snplBlockedStatus = ["FROZEN", "PENDING_REVIEW", "DECLINED"].includes(snplStatus);
  // SNPL is single-recipient only: the parent-order path cannot create a
  // settlement obligation (snpl_settlements.transaction_id is FK-bound to
  // transactions, not customer_orders). Backend enforces this too, returning
  // 422 QUOTE_SNPL_NOT_AVAILABLE. See ADR-002.
  const filledRecipientCount = slots.filter(Boolean).length;
  const snplMultiBlocked = filledRecipientCount > 1;
  const snplUsable =
    !snplBlockedStatus &&
    Number(snplAvailable || 0) >= MIN_SNPL_CAD;

  // If SNPL was chosen and the user then adds a second recipient, clear the
  // selection so they cannot submit an order the backend will reject (422).
  useEffect(() => {
    if (snplMultiBlocked && paymentMethod === "SNPL") {
      setPaymentMethod(null);
      setSnplCleared(true);
    }
    if (snplMultiBlocked === false) {
      setSnplCleared(false);
    }
  }, [snplMultiBlocked, paymentMethod]);

  function snplUnavailableReason() {
    if (snplStatus === "FROZEN") return "Your SNPL credit is temporarily frozen";
    if (snplStatus === "PENDING_REVIEW") return "Your assessment is under review";
    if (snplStatus === "DECLINED") return "Not available on your account right now";
    const amt = Number(snplAvailable || 0);
    if (amt <= 0) return "No credit available — unlock your limit first";
    return `Only CAD ${amt.toFixed(2)} available — minimum CAD ${MIN_SNPL_CAD.toFixed(2)}`;
  }

  function estimatedTotalCad() {
    let total = 0;
    slots.forEach((_, i) => {
      const amt = Number(amounts[i] || 0);
      if (amt > 0) total += direction === "SEND_CAD" ? amt : amt / FALLBACK_RATE;
    });
    return Math.round(total * 100) / 100;
  }

  const runningTotal = estimatedTotalCad();
  const remaining = Math.round((availableCap - runningTotal) * 100) / 100;
  const overCap = runningTotal > availableCap;
  const isEstimate = direction === "RECEIVE_KES";

  // ---------- steps ----------

  function chooseCount(n) {
    setRecipientCount(n);
    setSlots(Array(n).fill(null));
    setAmounts({});
    setError(null);
  }

  function choosePaymentJourney(method) {
    setPaymentMethod(method);
    setSnplCleared(false);
    setQuote(null);
    setOrder(null);
    setError(null);
    setFieldErrors({});
    setDirection("SEND_CAD");

    if (method === "SNPL") {
      // SNPL is inherently a single-recipient journey.
      chooseCount(1);
      return;
    }

    // Wallet supports multiple recipients, so the customer chooses the count
    // on the next step. Clear any state left by a previous SNPL selection.
    setRecipientCount(null);
    setSlots([]);
    setAmounts({});
  }

  const chosen = slots.filter(Boolean);
  const chosenIds = chosen.map((r) => r.id);

  function selectRecipient(index, recipient) {
    if (recipient && chosenIds.includes(recipient.id)) {
      setError(`${recipient.recipientName} is already on this order.`);
      return;
    }
    setError(null);
    const next = [...slots];
    next[index] = recipient;
    setSlots(next);
  }

  function setAmount(slotIndex, rawValue) {
    let value = rawValue;
    if (direction === "RECEIVE_KES") {
      value = rawValue.replace(/[^0-9]/g, "");
    } else {
      value = rawValue.replace(/[^0-9.]/g, "");
      const parts = value.split(".");
      if (parts.length > 2) value = parts[0] + "." + parts.slice(1).join("");
      if (parts[1] && parts[1].length > 2) value = parts[0] + "." + parts[1].slice(0, 2);
    }
    setAmounts({ ...amounts, [slotIndex]: value });
    setFieldErrors({ ...fieldErrors, [slotIndex]: null });
  }

  function validateAmounts() {
    const errs = {};
    slots.forEach((slot, i) => {
      if (!slot) {
        errs[i] = "Choose a recipient";
        return;
      }
      const amt = Number(amounts[i] || 0);
      if (amt === 0) {
        errs[i] = "Enter an amount to send";
        return;
      }
      if (direction === "SEND_CAD") {
        if (amt < LIMITS.CAD_MIN) errs[i] = `Minimum CAD ${LIMITS.CAD_MIN}`;
      } else {
        if (amt < LIMITS.MONEY_KES_MIN) errs[i] = `Minimum KES ${LIMITS.MONEY_KES_MIN}`;
        if (amt > LIMITS.MONEY_KES_MAX)
          errs[i] = `Maximum KES ${LIMITS.MONEY_KES_MAX.toLocaleString()} per transfer`;
      }
    });
    setFieldErrors(errs);
    return Object.values(errs).filter(Boolean).length === 0;
  }

  // ---------- quote / confirm ----------

  function buildQuotePayload() {
    const recipients = chosen.map((r, i) => {
      const amt = Number(amounts[i] || 0);
      const destinationAmount =
        direction === "SEND_CAD" ? Math.round(amt * FALLBACK_RATE) : amt;
      return {
        recipientId: r.id,
        destinationCountry: r.recipientCountry || "KE",
        destinationCurrency: "KES",
        products: [{ productType: "REMITTANCE", destinationAmount }],
      };
    });
    return { sourceCurrency: "CAD", paymentMethod, recipients };
  }

  async function handleGetQuote() {
    setError(null);
    if (!validateAmounts()) return;
    if (overCap) {
      setError(
        `This order is CAD ${runningTotal.toFixed(2)} but only CAD ${availableCap.toFixed(
          2
        )} is available. Reduce the amounts.`
      );
      return;
    }
    setLoading(true);
    try {
      const response = await createOrderQuote(buildQuotePayload());
      setQuote(response.quote);
      setStep(5);
    } catch (err) {
      setError(err.message || "Could not price this order");
    } finally {
      setLoading(false);
    }
  }

  function genIdempotencyKey() {
    return "order-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
  }

  async function handleConfirm() {
    setError(null);
    setLoading(true);
    // TD-15: create -> fund (reserve wallet money) -> send. Each step is
    // idempotent; if funding or sending fails, a retry reuses the SAME order
    // instead of creating a second one.
    let created = order;
    try {
      if (!created) {
        created = await createParentOrder(
          {
            quoteReference: quote.reference,
            recipients: chosen.map((r) => ({
              recipientId: r.id,
              destinationCountry: r.recipientCountry || "KE",
              notificationPhone: r.recipientPhone || null,
            })),
          },
          genIdempotencyKey()
        );
        setOrder(created);
      }
      const ref = created.order.reference;
      await fundParentOrder(ref);
      await fulfilParentOrder(ref);
      const fresh = await getParentOrder(ref);
      setOrder(fresh);
      setStep(6);
    } catch (err) {
      if (err.code === "VERIFICATION_REQUIRED" && err.data) {
        setVerification({ idVerified: !err.data.needsIdVerification, phoneVerified: !err.data.needsPhoneVerification });
      }
      const msg = err.message || "Could not complete this order";
      setError(created
        ? `${msg} Your order ${created.order.reference} has been saved; you can try again without being charged twice.`
        : msg);
    } finally {
      setLoading(false);
    }
  }

  /**
   * Upsell hand-off (ADR-001 Decision 3).
   * Carries only recipient IDS — never full recipient objects — so Mobile Top-Up
   * reloads current details from the backend and re-verifies ownership.
   * The top-up creates its OWN quote and Parent Order; this money order is
   * already accepted and must not be modified.
   */
  function handleAirtimeUpsell() {
    navigate("/airtime", {
      state: {
        handoffVersion: 1,
        source: "send-money-confirmation",
        sourceOrderReference: order && order.order ? order.order.reference : null,
        recipientIds: chosenIds.slice(0, MAX_RECIPIENTS),
      },
    });
  }

  // ---------- render ----------

  return (
    <div>
      <Navigation />
      <div className="p-6 max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold mb-1">Send Money</h1>
        <p className="text-gray-600 mb-4">
          Send money to up to three people in one order — one checkout.
        </p>
        <VerifyBanner verification={verification} />

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4 text-sm">
            {error}
          </div>
        )}

        {/* STEP 1 — payment journey */}
        {step === 1 && (
          <div className="border p-4 rounded shadow">
            <h2 className="font-semibold text-lg mb-1">
              Choose your payment journey
            </h2>
            <p className="text-sm text-gray-500 mb-3">
              Each order uses one funding source — Pay Later and wallet funds
              are not combined.
            </p>

            {contextLoading ? (
              <p className="text-gray-500">Loading your available funds…</p>
            ) : (
              <>
                <label
                  className={
                    "block border rounded p-3 mb-2 cursor-pointer " +
                    (paymentMethod === "SNPL"
                      ? "border-blue-600 bg-blue-50"
                      : "border-gray-300") +
                    (snplUsable ? "" : " opacity-50")
                  }
                >
                  <input
                    type="radio"
                    name="paymentJourney"
                    className="mr-2"
                    disabled={!snplUsable}
                    checked={paymentMethod === "SNPL"}
                    onChange={() => choosePaymentJourney("SNPL")}
                  />
                  <span className="font-semibold">
                    Send Now Pay Later (SNPL)
                  </span>
                  <div className="text-sm text-gray-600 ml-6">
                    {snplUsable
                      ? `Pay one recipient using CAD ${Number(
                          snplAvailable
                        ).toFixed(2)} available credit`
                      : snplUnavailableReason()}
                  </div>
                </label>

                <label
                  className={
                    "block border rounded p-3 mb-4 cursor-pointer " +
                    (paymentMethod === "WALLET"
                      ? "border-blue-600 bg-blue-50"
                      : "border-gray-300") +
                    (Number(walletCad || 0) > 0 ? "" : " opacity-50")
                  }
                >
                  <input
                    type="radio"
                    name="paymentJourney"
                    className="mr-2"
                    disabled={!(Number(walletCad || 0) > 0)}
                    checked={paymentMethod === "WALLET"}
                    onChange={() => choosePaymentJourney("WALLET")}
                  />
                  <span className="font-semibold">
                    Send Now Pay Now (Wallet)
                  </span>
                  <div className="text-sm text-gray-600 ml-6">
                    {Number(walletCad || 0) > 0
                      ? `Pay up to three recipients using CAD ${Number(
                          walletCad
                        ).toFixed(2)} available`
                      : "No wallet balance — load funds first"}
                  </div>
                </label>
              </>
            )}

            <button
              className="bg-blue-600 text-white p-2 w-full rounded disabled:opacity-50"
              disabled={!paymentMethod}
              onClick={() =>
                setStep(paymentMethod === "SNPL" ? 3 : 2)
              }
            >
              Continue
            </button>
          </div>
        )}

        {/* STEP 2 — wallet recipient count */}
        {step === 2 && paymentMethod === "WALLET" && (
          <div className="border p-4 rounded shadow">
            <h2 className="font-semibold text-lg mb-1">
              How many recipients?
            </h2>
            <p className="text-sm text-gray-500 mb-3">
              A wallet-funded order can include up to {MAX_RECIPIENTS} recipients.
            </p>

            <div className="grid grid-cols-3 gap-2 mb-4">
              {[1, 2, 3].map((n) => (
                <label
                  key={n}
                  className={
                    "border rounded p-3 text-center cursor-pointer " +
                    (recipientCount === n
                      ? "border-blue-600 bg-blue-50"
                      : "border-gray-300")
                  }
                >
                  <input
                    type="radio"
                    name="recipientCount"
                    className="mr-2"
                    checked={recipientCount === n}
                    onChange={() => chooseCount(n)}
                  />
                  {n} {n === 1 ? "recipient" : "recipients"}
                </label>
              ))}
            </div>

            <div className="flex gap-2">
              <button
                className="border p-2 flex-1 rounded"
                onClick={() => setStep(1)}
              >
                Back
              </button>
              <button
                className="bg-blue-600 text-white p-2 flex-1 rounded disabled:opacity-50"
                disabled={!recipientCount}
                onClick={() => setStep(3)}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 — direction */}
        {step === 3 && (
          <div className="border p-4 rounded shadow">
            <h2 className="font-semibold text-lg mb-3">How would you like to enter amounts?</h2>

            <label
              className={
                "block border rounded p-3 mb-2 cursor-pointer " +
                (direction === "SEND_CAD" ? "border-blue-600 bg-blue-50" : "border-gray-300")
              }
            >
              <input
                type="radio"
                name="dir"
                className="mr-2"
                checked={direction === "SEND_CAD"}
                onChange={() => setDirection("SEND_CAD")}
              />
              I want to send (CAD)
            </label>

            <label
              className={
                "block border rounded p-3 mb-4 cursor-pointer " +
                (direction === "RECEIVE_KES" ? "border-blue-600 bg-blue-50" : "border-gray-300")
              }
            >
              <input
                type="radio"
                name="dir"
                className="mr-2"
                checked={direction === "RECEIVE_KES"}
                onChange={() => setDirection("RECEIVE_KES")}
              />
              Recipient receives (KES)
            </label>

            <div className="flex gap-2">
              <button
                className="border p-2 flex-1 rounded"
                onClick={() => setStep(paymentMethod === "SNPL" ? 1 : 2)}
              >
                Back
              </button>
              <button className="bg-blue-600 text-white p-2 flex-1 rounded" onClick={() => setStep(4)}>
                Continue
              </button>
            </div>
          </div>
        )}

        {/* STEP 4 — recipients + amounts */}
        {step === 4 && (
          <div>
            <div
              className={
                "border p-3 rounded mb-4 text-sm " +
                (overCap && paymentMethod && availableCap > 0
                  ? "bg-red-50 border-red-300 text-red-800"
                  : "bg-gray-50 border-gray-200 text-gray-700")
              }
            >
              <div className="flex justify-between font-semibold">
                <span>
                  {!paymentMethod
                    ? "Order total"
                    : paymentMethod === "SNPL"
                    ? "SNPL credit"
                    : "Wallet balance"}
                </span>
                <span>
                  {!paymentMethod
                    ? `CAD ${runningTotal.toFixed(2)}`
                    : availableCap <= 0
                    ? `CAD ${runningTotal.toFixed(2)}`
                    : `CAD ${runningTotal.toFixed(2)} of ${availableCap.toFixed(2)} used`}
                </span>
              </div>
              <div className="flex justify-between">
                <span>{isEstimate ? "Estimated — final price at review" : ""}</span>
                <span>
                  {!paymentMethod
                    ? "Choose how you are paying below"
                    : availableCap <= 0
                    ? (paymentMethod === "SNPL"
                        ? "No credit available"
                        : "No wallet balance — add funds first")
                    : overCap
                    ? `Over by CAD ${Math.abs(remaining).toFixed(2)}`
                    : `CAD ${remaining.toFixed(2)} remaining`}
                </span>
              </div>
              {snplCleared && (
                <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  Pay Later is for one recipient at a time, so it was turned off
                  when you added another. Choose a payment method below.
                </div>
              )}
            </div>

            {slots.map((slot, i) => (
              <div key={i} className="border p-4 rounded shadow mb-3">
                <p className="font-semibold mb-2">Recipient {i + 1}</p>

                <RecipientSelector
                  onSelect={(r) => selectRecipient(i, r)}
                  selectedRecipient={slot}
                  excludeIds={chosenIds.filter((id) => !(slot && slot.id === id))}
                />

                {slot && (
                  <>
                    <p className="text-sm text-green-700 mt-2 mb-3">
                      ✓ {slot.recipientName} — {slot.recipientPhone}
                    </p>

                    <label className="block text-sm font-semibold mb-1">
                      Amount ({direction === "SEND_CAD" ? "CAD" : "KES"}) — to mobile money
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      maxLength={direction === "SEND_CAD" ? 7 : 6}
                      className="border p-2 w-full rounded mb-1"
                      placeholder={direction === "SEND_CAD" ? "e.g. 200" : "e.g. 20000"}
                      value={amounts[i] || ""}
                      onChange={(e) => setAmount(i, e.target.value)}
                    />
                    {fieldErrors[i] ? (
                      <p className="text-xs text-red-600 mb-2">{fieldErrors[i]}</p>
                    ) : (
                      <p className="text-xs text-gray-500 mb-2">
                        {direction === "SEND_CAD"
                          ? `Minimum CAD ${LIMITS.CAD_MIN}`
                          : `KES ${LIMITS.MONEY_KES_MIN} – ${LIMITS.MONEY_KES_MAX.toLocaleString()}`}
                      </p>
                    )}
                  </>
                )}
              </div>
            ))}

            <div className="bg-blue-50 border border-blue-200 text-blue-800 p-3 rounded mb-4 text-xs">
              Sending airtime? Do it in <strong>Mobile Top-Up</strong> — we'll offer it
              right after you confirm this order.
            </div>

            <div className="flex gap-2">
              <button className="border p-2 flex-1 rounded" onClick={() => setStep(3)}>
                Back
              </button>
              <button
                className="bg-blue-600 text-white p-2 flex-1 rounded disabled:opacity-50"
                onClick={handleGetQuote}
                disabled={loading || overCap || !online}
              >
                {loading ? "Pricing…" : "Get quote"}
              </button>
            </div>
          </div>
        )}

        {/* STEP 5 — review */}
        {step === 5 && quote && (
          <div>
            <h2 className="font-semibold text-lg mb-3">Review your order</h2>

            <div className="border p-4 rounded shadow mb-3">
              {quote.items.map((item, idx) => {
                const r = chosen[item.recipientIndex - 1];
                return (
                  <div key={idx} className="flex justify-between py-2 border-b text-sm">
                    <div>
                      <div className="font-semibold">
                        {r ? r.recipientName : `Recipient ${item.recipientIndex}`}
                      </div>
                      <div className="text-gray-500">
                        {item.destinationCurrency} {item.destinationAmount}
                      </div>
                    </div>
                    <div className="text-right">
                      CAD {item.customerPayAmount}
                    </div>
                  </div>
                );
              })}

              <div className="text-sm mt-3">
                {Number(quote.breakdown.transferFee) > 0 && (
                  <Row label="Transfer fee (under CAD 100)" value={quote.breakdown.transferFee} />
                )}
                {Number(quote.breakdown.multiRecipientFee) > 0 && (
                  <Row label="Multi-recipient fee" value={quote.breakdown.multiRecipientFee} />
                )}
                {Number(quote.breakdown.snplFee) > 0 && (
                  <Row label="SNPL fee" value={quote.breakdown.snplFee} />
                )}
                {/* Show a "Total fees" line only when more than one kind of fee applies,
                    so a single fee is never listed twice. */}
                {[quote.breakdown.transferFee, quote.breakdown.multiRecipientFee, quote.breakdown.snplFee]
                  .filter((f) => Number(f) > 0).length > 1 && (
                  <Row label="Total fees" value={quote.breakdown.totalFees} />
                )}
                <div className="flex justify-between py-2 font-bold text-base border-t mt-1">
                  <span>Total</span>
                  <span>CAD {quote.breakdown.totalAmount}</span>
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500 mb-3">
              Paying with {paymentMethod === "SNPL" ? "SNPL credit" : "wallet balance"}. This
              quote expires at {new Date(quote.expiresAt).toLocaleTimeString()}.
            </p>

            <div className="flex gap-2">
              <button
                className="border p-2 flex-1 rounded"
                onClick={() => {
                  setQuote(null);
                  setStep(4);
                }}
              >
                Back
              </button>
              <button
                className="bg-green-600 text-white p-2 flex-1 rounded disabled:opacity-50"
                onClick={handleConfirm}
                disabled={loading || !online || Boolean(verification && !(verification.idVerified && verification.phoneVerified))}
              >
                {loading ? "Sending…" : order ? "Try again" : "Confirm and send"}
              </button>
            </div>
          </div>
        )}

        {/* STEP 6 — done + upsell */}
        {step === 6 && order && (
          <div>
            <h2 className="font-semibold text-lg mb-3">Order sent</h2>

            <div className="border p-4 rounded shadow mb-3 text-sm">
              <div className="flex justify-between py-1 border-b">
                <span className="text-gray-600">Reference</span>
                <span className="font-mono">{order.order.reference}</span>
              </div>
              <div className="flex justify-between py-1 border-b">
                <span className="text-gray-600">Status</span>
                <span className="font-semibold">{order.order.status}</span>
              </div>
              <div className="flex justify-between py-1 border-b">
                <span className="text-gray-600">Total</span>
                <span>
                  {order.order.sourceCurrency} {order.order.totalAmount}
                </span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-gray-600">Payment method</span>
                <span>{order.order.paymentMethod}</span>
              </div>
            </div>

            {(order.recipients || []).map((rg, i) => (
              <div key={i} className="border p-3 rounded mb-2 text-sm">
                <p className="font-semibold mb-1">
                  {chosen[rg.displayOrder - 1]
                    ? chosen[rg.displayOrder - 1].recipientName
                    : `Recipient ${rg.displayOrder}`}
                </p>
                {(rg.items || []).map((it) => (
                  <div key={it.itemReference} className="flex justify-between text-gray-600">
                    <span>
                      {it.destinationCurrency} {it.destinationAmount}
                    </span>
                    <span>{it.status}</span>
                  </div>
                ))}
              </div>
            ))}

            <OrderOutcome order={order} />

            {/* Upsell hand-off — creates a SEPARATE quote and order */}
            <div className="bg-green-50 border border-green-200 p-4 rounded mb-3">
              <p className="font-semibold text-green-900 mb-1">
                Also send them airtime?
              </p>
              <p className="text-sm text-green-800 mb-3">
                Top up the same {chosen.length === 1 ? "person" : "people"} with mobile
                credit. This is a separate order, priced on its own.
              </p>
              <button
                className="bg-green-600 text-white p-2 w-full rounded"
                onClick={handleAirtimeUpsell}
              >
                📱 Add mobile top-up
              </button>
              <button
                className="text-sm text-green-900 underline mt-2 w-full"
                onClick={() => navigate("/dashboard")}
              >
                No thanks
              </button>
            </div>

            <button
              className="border p-2 w-full rounded"
              onClick={() => navigate("/dashboard")}
            >
              Back to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-1">
      <span className="text-gray-600">{label}</span>
      <span>CAD {value}</span>
    </div>
  );
}
